/**
 * Sign-in with Cognito's hosted UI: OAuth 2.0 authorization code + PKCE, no SDK (RELAY-937).
 *
 * The flow, end to end:
 *   1. `beginSignIn()` makes a PKCE verifier + state, parks them in sessionStorage and sends
 *      the browser to `/oauth2/authorize` on the hosted UI (which only offers Google —
 *      infra/modules/cognito). Cognito sends it back to `/auth/callback?code&state`.
 *   2. `completeSignIn()` checks the state, exchanges the code at `/oauth2/token` (a public
 *      client: no secret, the verifier IS the proof) and stores the tokens.
 *   3. `getIdToken()` is what the API client calls per request: it hands back the ID token
 *      while it is fresh and uses the refresh token when it is about to expire. The API
 *      verifies the ID token (`aws-jwt-verify`, tokenUse 'id') — see apps/api/src/integrations/cognito.ts.
 *
 * Tokens live in localStorage so a reload stays signed in; a deactivated user's tokens are
 * rejected server-side (`sessions_revoked_at`), so the client never needs a blacklist.
 */

export interface AuthConfig {
  /** Hosted-UI origin, no trailing slash. */
  domain: string;
  clientId: string;
  redirectUri: string;
}

export interface Tokens {
  idToken: string;
  refreshToken?: string | undefined;
  /** Epoch ms after which `idToken` is no longer presented. */
  expiresAt: number;
}

export class AuthFlowError extends Error {
  constructor(
    message: string,
    readonly code: 'state_mismatch' | 'missing_code' | 'token_exchange_failed' | 'not_configured',
  ) {
    super(message);
    this.name = 'AuthFlowError';
  }
}

const TOKENS_KEY = 'crm.auth';
const PKCE_KEY = 'crm.pkce';
/** Refresh when this close to expiry, so a request never carries a token that dies in flight. */
const REFRESH_SKEW_MS = 60_000;

/**
 * The Cognito settings: the deployed environment's /config.js first (TS-1348: one build serves
 * staging and production), else what Vite inlined at build time (local `.env`). Null = sign-in
 * is not configured here.
 */
export function authConfig(
  env: ImportMetaEnv = import.meta.env,
  runtime: CrmRuntimeConfig | undefined = window.__CRM_CONFIG__,
): AuthConfig | null {
  const domain = (runtime?.cognitoDomain || env.VITE_COGNITO_DOMAIN)?.replace(/\/$/, '');
  const clientId = runtime?.cognitoClientId || env.VITE_COGNITO_CLIENT_ID;
  if (!domain || !clientId) return null;
  return { domain, clientId, redirectUri: `${window.location.origin}/auth/callback` };
}

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch (err) {
    // Private mode / blocked storage: behave as signed out rather than crash the app.
    console.warn('storage read failed', key, err);
    return null;
  }
}

function writeJson(storage: Storage, key: string, value: unknown) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn('storage write failed', key, err);
  }
}

export const tokenStore = {
  get: (): Tokens | null => readJson<Tokens>(localStorage, TOKENS_KEY),
  set: (tokens: Tokens) => writeJson(localStorage, TOKENS_KEY, tokens),
  clear: () => localStorage.removeItem(TOKENS_KEY),
};

/** `exp` of a JWT as epoch ms, or `fallback` when the token is not a readable JWT. */
export function jwtExpiry(token: string, fallback = Date.now() + 55 * 60_000): number {
  const part = token.split('.')[1];
  if (!part) return fallback;
  try {
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: unknown;
    };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : fallback;
  } catch (err) {
    console.debug('token is not a readable JWT; using the fallback expiry', err);
    return fallback;
  }
}

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function buildAuthorizeUrl(
  cfg: AuthConfig,
  params: { state: string; challenge: string },
): string {
  const q = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: 'openid email profile',
    state: params.state,
    code_challenge: params.challenge,
    code_challenge_method: 'S256',
  });
  return `${cfg.domain}/oauth2/authorize?${q.toString()}`;
}

/** Step 1: park the PKCE state and leave for the hosted UI. `returnTo` is where to land afterwards. */
export async function beginSignIn(
  cfg: AuthConfig,
  opts: { returnTo?: string; navigate?: (url: string) => void } = {},
): Promise<string> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(48)));
  const state = base64url(crypto.getRandomValues(new Uint8Array(16)));
  writeJson(sessionStorage, PKCE_KEY, { verifier, state, returnTo: opts.returnTo ?? '/' });
  const url = buildAuthorizeUrl(cfg, { state, challenge: await pkceChallenge(verifier) });
  (opts.navigate ?? ((u: string) => window.location.assign(u)))(url);
  return url;
}

interface TokenResponse {
  id_token: string;
  refresh_token?: string;
  expires_in?: number;
}

async function tokenRequest(
  cfg: AuthConfig,
  body: Record<string, string>,
  fetchImpl: typeof fetch,
): Promise<Tokens> {
  let res: Response;
  try {
    res = await fetchImpl(`${cfg.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ client_id: cfg.clientId, ...body }).toString(),
    });
  } catch (err) {
    throw new AuthFlowError(
      `Token endpoint unreachable: ${err instanceof Error ? err.message : 'network error'}`,
      'token_exchange_failed',
    );
  }
  if (!res.ok)
    throw new AuthFlowError(`Token exchange failed (${res.status})`, 'token_exchange_failed');
  const json = (await res.json()) as Partial<TokenResponse>;
  if (typeof json.id_token !== 'string')
    throw new AuthFlowError('Token response carried no id_token', 'token_exchange_failed');
  const expiresAt =
    typeof json.expires_in === 'number'
      ? Date.now() + json.expires_in * 1000
      : jwtExpiry(json.id_token);
  return {
    idToken: json.id_token,
    refreshToken: json.refresh_token ?? body.refresh_token,
    expiresAt,
  };
}

/** Step 2: back from the hosted UI. Resolves to where the user wanted to go. */
export async function completeSignIn(
  cfg: AuthConfig,
  callbackUrl: URL,
  fetchImpl: typeof fetch = fetch,
): Promise<{ returnTo: string }> {
  const code = callbackUrl.searchParams.get('code');
  const state = callbackUrl.searchParams.get('state');
  const pkce = readJson<{ verifier: string; state: string; returnTo: string }>(
    sessionStorage,
    PKCE_KEY,
  );
  sessionStorage.removeItem(PKCE_KEY);
  if (!code) throw new AuthFlowError('No authorization code in the callback', 'missing_code');
  if (!pkce || pkce.state !== state)
    throw new AuthFlowError(
      'Sign-in state did not match (stale or forged callback)',
      'state_mismatch',
    );
  const tokens = await tokenRequest(
    cfg,
    {
      grant_type: 'authorization_code',
      code,
      redirect_uri: cfg.redirectUri,
      code_verifier: pkce.verifier,
    },
    fetchImpl,
  );
  tokenStore.set(tokens);
  return { returnTo: pkce.returnTo || '/' };
}

let refreshing: Promise<Tokens | null> | null = null;

/** The bearer the API client sends, refreshed when near expiry. Null = signed out. */
export async function getIdToken(
  cfg: AuthConfig | null = authConfig(),
  fetchImpl: typeof fetch = fetch,
  now = Date.now(),
): Promise<string | null> {
  const tokens = tokenStore.get();
  if (!tokens) return null;
  if (tokens.expiresAt - now > REFRESH_SKEW_MS) return tokens.idToken;
  if (!tokens.refreshToken || !cfg) {
    // Nothing to refresh with: present it anyway and let the API's 401 end the session.
    return tokens.idToken;
  }
  // One refresh at a time: concurrent requests at the expiry edge share the exchange.
  refreshing ??= tokenRequest(
    cfg,
    { grant_type: 'refresh_token', refresh_token: tokens.refreshToken },
    fetchImpl,
  )
    .then((fresh) => {
      tokenStore.set(fresh);
      return fresh;
    })
    .catch((err: unknown) => {
      console.warn('token refresh failed; signing out', err);
      tokenStore.clear();
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  const fresh = await refreshing;
  return fresh?.idToken ?? null;
}

/** Forget the session here and, when the hosted UI is in play, there too. */
export function signOut(
  cfg: AuthConfig | null = authConfig(),
  navigate: (url: string) => void = (u) => window.location.assign(u),
) {
  tokenStore.clear();
  if (cfg) {
    const q = new URLSearchParams({ client_id: cfg.clientId, logout_uri: window.location.origin });
    navigate(`${cfg.domain}/logout?${q.toString()}`);
  } else {
    navigate('/sign-in');
  }
}
